import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FirebaseContext } from '../../components/Firebase';
import ReactPDF, { Document, Page, Text, View, StyleSheet, Font, Image, Link, pdf } from '@react-pdf/renderer';
import { Document as RpDocument, Page as RpPage, pdfjs } from 'react-pdf/dist/esm/entry.webpack';
import { REACT_APP_BASE_URL } from '../../env';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import Spinner from '../../components/Spinner';

import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faForward, faBackward } from '@fortawesome/free-solid-svg-icons';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons';

import { Theme, createStyles, makeStyles, useTheme } from '@material-ui/core/styles';
import { ThemeContext } from '../../components/Session';
import { darken, lighten, rgbToHex } from '@material-ui/core/styles/colorManipulator';

import Utilities from '../../utils/utilities';

import moment from 'moment';
import { QuoteCharge, ChargeCategory, FreightForwarder, Quote, Image as ImageType } from '../../../types';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import font from '../../fonts/SourceSansPro-Regular-exported.ttf';
import { useQuoteCurrencyConversion } from '../../hooks/useQuoteCurrencyConversion';

// rename helper for react18 overload - see https://github.com/diegomura/react-pdf/issues/1779
const DocumentX: any = Document;
const PageX: any = Page;

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

Font.register({ family: 'Source Sans Pro', format: 'truetype', src: font });
Font.registerHyphenationCallback((word) => [word]);

// Create styles
const usePdfStyles = (theme: Theme) =>
  StyleSheet.create({
    page: {
      // flexDirection: 'row',
      // display: 'block',
      // backgroundColor: rgbToHex(theme.palette.primary.lighter)
      // position: 'relative',
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(3), //allow for page numbers
      // marginBottom: theme.spacing(4),
      // width: '100%',
      // maxHeight: '100%',
      fontFamily: 'Source Sans Pro',
    },
    section: {
      margin: 10,
      padding: 10,
      flexGrow: 1,
    },
    row: {
      flexDirection: 'row',
      display: 'flex',
      flexWrap: 'wrap',
    },
    col: {
      flexGrow: 1,
      maxWidth: '100%',
      flexBasis: 0,
    },
    bottomMargin: {
      marginBottom: theme.spacing(1.5),
    },
    marginTop: {
      marginTop: theme.spacing(1),
    },
    gutterBottom: {
      marginBottom: theme.spacing(0.5),
    },
    rightAlign: {
      justifyContent: 'flex-end',
    },
    textRight: {
      textAlign: 'right',
    },
    logo: {
      objectFit: 'scale-down',
      objectPosition: '0% 50%', // hard left, 50% vertical (middle)
      maxWidth: '150px',
      maxHeight: '35px',
    },
    subtitle2: {
      color: '#07020D',
      opacity: 0.4,
      fontSize: 8,
      fontWeight: 500,
      lineHeight: 1.57,
    },
    bold: {
      fontWeight: 600,
    },
    opacity1: {
      opacity: 1,
    },
    caption: {
      fontSize: 8,
      // fontWeight: 1000,
      lineHeight: 1.57,
    },
    primaryText: {
      color: theme.palette.primary.main,
    },
    h4: {
      fontSize: 16,
      // fontWeight: 600,
      opacity: 0.89,
    },
    h5: {
      fontSize: 10,
      // fontWeight: 600,
    },
    body: {
      fontSize: 10,
    },
    muted: {
      color: theme.palette.grey[400],
    },
    locationGroup: {
      backgroundColor: rgbToHex((theme.palette.primary as any).lighter),
      borderRadius: theme.spacing(2),
      marginRight: theme.spacing(2),
      paddingVertical: theme.spacing(1),
      paddingHorizontal: theme.spacing(2),
    },
    agentBox: {
      marginTop: theme.spacing(1),
    },
    divider: {
      // borderTop: '2px solid black',
      borderTopStyle: 'solid',
      borderTopColor: theme.palette.grey[200],
      borderTopWidth: 1,
      marginVertical: theme.spacing(1),
    },
    tableHeaderRow: {
      marginTop: theme.spacing(1),
      borderTopLeftRadius: theme.spacing(2),
      borderTopRightRadius: theme.spacing(2),
      backgroundColor: rgbToHex(theme.palette.primary.dark),
      color: theme.palette.common.white,
      fontSize: 8,
    },
    tableCellHead: {
      paddingHorizontal: theme.spacing(0.5),
      paddingVertical: theme.spacing(1),
    },
    tableBody: {
      backgroundColor: theme.palette.grey[200],
      // marginBottom: theme.spacing(2),
    },
    tableCell: {
      paddingHorizontal: theme.spacing(0.5),
      paddingVertical: theme.spacing(0.5),
      margin: 1,
      // borderStyle: 'solid',
      // borderColor: theme.palette.grey[200],
      // borderWidth: 2,
      fontSize: 10,
      backgroundColor: theme.palette.common.white,
    },
    pageNumber: {
      fontSize: 10,
      height: theme.spacing(2),
      textAlign: 'center',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
    },
  });

type PdfFieldProps = {
  fieldName: string;
  value: string | string[] | undefined;
  pdfStyles: ReactPDF.Styles;
  maxWidth?: string;
  gutterBottom?: boolean;
};
const PdfField = ({ fieldName, value, maxWidth = '20%', gutterBottom = true, pdfStyles }: PdfFieldProps) => {
  const theme = useTheme();
  const arrayValues = Array.isArray(value) ? value : [value];
  return value && value.length > 0 ? (
    <View style={{ ...pdfStyles.col, marginBottom: gutterBottom ? theme.spacing(0.5) : 0, maxWidth: maxWidth }}>
      <Text style={{ ...pdfStyles.caption, ...pdfStyles.primaryText }}>{fieldName}</Text>
      {arrayValues.map((arrayValue, index) => (
        <Text key={index} style={pdfStyles.body}>
          {arrayValue}
        </Text>
      ))}
    </View>
  ) : null;
};

// Create Document Component
type MyDocumentProps = {
  theme: Theme;
  currentFreightForwarder: FreightForwarder;
  quote: Quote;
  chargeCategories: ChargeCategory[];
  hasBaseFee: boolean;
  hasMinimum: boolean;
};
const MyDocument = (props: MyDocumentProps) => {
  const { theme, currentFreightForwarder, quote, chargeCategories, hasBaseFee, hasMinimum } = props;
  const pdfStyles = usePdfStyles(theme);
  const { conversionFactorsUsed, displayCurrency, calcSellTotal } = useQuoteCurrencyConversion(
    quote,
    currentFreightForwarder
  );

  // note adding the "?pdf" to teh end is just so that a cahced version of the logo is not served as the logo
  // will have appeared elsewhere in the app and then it get a CORS issue
  const imagesrc = `${Utilities.getPhotoUrl(currentFreightForwarder.logo_images as ImageType[], 'logo')}?pdf`;
  const conditions_of_trade = `${REACT_APP_BASE_URL}/customer-view/${currentFreightForwarder._id}/quotes/${quote._id}/conditions_of_trade`;

  return (
    <DocumentX>
      <PageX size="A4" style={pdfStyles.page}>
        <View style={{ ...pdfStyles.row, ...pdfStyles.bottomMargin }}>
          <View style={pdfStyles.col}>
            <Image style={pdfStyles.logo} src={imagesrc} />
          </View>
          <View style={pdfStyles.col}>
            <View style={{ ...pdfStyles.row, ...pdfStyles.rightAlign }}>
              <Text style={pdfStyles.h4}>
                {quote.movementType && quote.movementType.length > 0 ? `${quote.movementType} ` : ''}Quote #
                {quote.quoteNumber}
              </Text>
            </View>
          </View>
        </View>
        <View style={pdfStyles.row}>
          <View style={pdfStyles.col}>
            <Text style={{ ...pdfStyles.primaryText, ...pdfStyles.caption }}>QUOTE FOR</Text>
            {quote.client && (
              <View style={pdfStyles.row}>
                <View>
                  <Text style={pdfStyles.h5}>{quote.client.name}</Text>
                  <Text style={pdfStyles.h5}>{quote.client.address}</Text>
                  <Text style={pdfStyles.h5}>
                    {quote.client.suburb}
                    {quote.client.city ? `\n${quote.client.city}` : ''}
                    {quote.client.postCode ? `, ${quote.client.postCode}` : ''}
                  </Text>
                  <Text style={pdfStyles.h5}>{quote.client.country}</Text>
                </View>
              </View>
            )}
          </View>
          <View style={pdfStyles.col}>
            <View style={{ ...pdfStyles.row, ...pdfStyles.rightAlign }}>
              <View>
                <Text style={[pdfStyles.h5, pdfStyles.muted]}>{currentFreightForwarder.name}</Text>
                <Text style={[pdfStyles.h5, pdfStyles.muted]}>{currentFreightForwarder.address}</Text>
                {/* <Text style={[pdfStyles.h5, pdfStyles.muted]}>{currentFreightForwarder.suburb}</Text> */}
                <Text style={[pdfStyles.h5, pdfStyles.muted]}>
                  {currentFreightForwarder.city}
                  {currentFreightForwarder.postCode && `, ${currentFreightForwarder.postCode}`}
                </Text>
                <Text style={[pdfStyles.h5, pdfStyles.muted]}>{currentFreightForwarder.country}</Text>
                <Text style={[pdfStyles.h5, pdfStyles.muted]}>{currentFreightForwarder.phone_number}</Text>
              </View>
            </View>
          </View>
        </View>
        <View style={{ ...pdfStyles.row, ...pdfStyles.marginTop }}>
          {quote.clientDisplay && quote.clientDisplay.container === true && (
            <PdfField
              pdfStyles={pdfStyles}
              fieldName="CONTAINER"
              value={quote.containers && quote.containers.map((c) => c.name)}
            />
          )}
          <PdfField
            pdfStyles={pdfStyles}
            fieldName="START DATE"
            value={quote.validFrom ? moment(quote.validFrom).format('DD/MM/YYYY') : ''}
          />
          <PdfField
            pdfStyles={pdfStyles}
            fieldName="EXPIRY DATE"
            value={quote.validUntil ? moment(quote.validUntil).format('DD/MM/YYYY') : ''}
          />
          {quote.clientDisplay && quote.clientDisplay.trade_lane === true && (
            <PdfField
              pdfStyles={pdfStyles}
              fieldName="SERVICE DESCRIPTION"
              value={quote.service && quote.service.name}
            />
          )}
          {quote.clientDisplay && quote.clientDisplay.shipping_line === true && (
            <PdfField
              pdfStyles={pdfStyles}
              fieldName="SERVICE PROVIDER"
              value={quote.service && quote.service.shipping_line}
            />
          )}
          {quote.mode === 'AIR' && (
            <PdfField
              pdfStyles={pdfStyles}
              fieldName="WEIGHT CHARGED"
              value={`${quote.weight && quote.weight.calculated} ${quote.weight && quote.weight.unit}`}
            />
          )}
          {(quote.mode === 'AIR' || quote.mode === 'LCL') && (
            <PdfField
              pdfStyles={pdfStyles}
              fieldName="ACTUAL WEIGHT"
              value={`${quote.weight && quote.weight.gross && quote.weight.gross.toFixed(2)} ${
                quote.weight && quote.weight.unit
              }`}
            />
          )}
          {(quote.mode === 'AIR' || quote.mode === 'LCL') && (
            <PdfField
              pdfStyles={pdfStyles}
              fieldName="VOLUME"
              value={`${quote.volume && quote.volume.total && quote.volume.total.toFixed(2)} ${
                quote.volume && quote.volume.unit
              }`}
            />
          )}
        </View>
        <View style={{ ...pdfStyles.row }}>
          <PdfField pdfStyles={pdfStyles} fieldName="MODE" value={quote.mode} />
          <PdfField pdfStyles={pdfStyles} fieldName="INCOTERM" value={quote.incoterm && quote.incoterm.code} />
          <PdfField
            pdfStyles={pdfStyles}
            fieldName="FREQUENCY"
            value={
              quote.service && quote.service.frequency && quote.service.frequency !== 'None'
                ? quote.service.frequency
                : undefined
            }
          />
          <PdfField pdfStyles={pdfStyles} fieldName="PREPARED FOR" value={quote.prepared_for} />
          <PdfField pdfStyles={pdfStyles} fieldName="PREPARED BY" value={quote.prepared_by} />
          {quote.quantity && quote.quantity.quantityType && quote.quantity.value ? (
            <PdfField
              pdfStyles={pdfStyles}
              fieldName="QUANTITY"
              value={`${quote.quantity.value} ${quote.quantity.quantityType}`}
            />
          ) : null}
        </View>
        <View style={{ ...pdfStyles.row }}>
          <View style={{ ...pdfStyles.locationGroup, ...pdfStyles.col }}>
            <View style={{ ...pdfStyles.row }}>
              <View style={pdfStyles.col}>
                {quote.service && quote.service.origin ? (
                  <>
                    <View>
                      <PdfField
                        pdfStyles={pdfStyles}
                        fieldName="ORIGIN PORT"
                        value={`${quote.service.origin.country && quote.service.origin.country.code}${
                          quote.service.origin.port && quote.service.origin.port.code
                        }`}
                        maxWidth="100%"
                        gutterBottom={false}
                      />
                    </View>
                    <Text style={{ ...pdfStyles.body }}>
                      {quote.service.origin.port && quote.service.origin.port.name}
                    </Text>
                  </>
                ) : null}
              </View>
              <View style={pdfStyles.col}>
                {quote.client &&
                  quote.client.origin_location &&
                  quote.clientDisplay &&
                  quote.clientDisplay.origin_location === true && (
                    <View>
                      <Text style={{ ...pdfStyles.caption, ...pdfStyles.primaryText }}>LOCATION</Text>
                      <Text style={{ ...pdfStyles.body }}>{quote.client.origin_location.name}</Text>
                      <Text style={{ ...pdfStyles.body }}>{quote.client.origin_location.address}</Text>
                      <Text style={{ ...pdfStyles.body }}>
                        {quote.client.origin_location.city}
                        {quote.client.origin_location.region && `, ${quote.client.origin_location.region}`}
                        {quote.client.origin_location.postCode && `, ${quote.client.origin_location.postCode}`}
                      </Text>
                      <Text style={{ ...pdfStyles.body }}>{quote.client.origin_location.country}</Text>
                    </View>
                  )}
              </View>
            </View>
          </View>
          <View style={{ ...pdfStyles.locationGroup, ...pdfStyles.col }}>
            <View style={{ ...pdfStyles.row }}>
              <View style={pdfStyles.col}>
                {quote.service && quote.service.destination ? (
                  <>
                    <View>
                      <PdfField
                        pdfStyles={pdfStyles}
                        fieldName="DESTINATION PORT"
                        value={`${quote.service.destination.country && quote.service.destination.country.code}${
                          quote.service.destination.port && quote.service.destination.port.code
                        }`}
                        maxWidth="100%"
                        gutterBottom={false}
                      />
                    </View>
                    <Text style={{ ...pdfStyles.body }}>
                      {quote.service.destination.port && quote.service.destination.port.name}
                    </Text>
                  </>
                ) : null}
              </View>
              <View style={pdfStyles.col}>
                {quote.client &&
                  quote.client.destination_location &&
                  quote.clientDisplay &&
                  quote.clientDisplay.destination_location === true && (
                    <View>
                      <Text style={{ ...pdfStyles.caption, ...pdfStyles.primaryText }}>LOCATION</Text>
                      <Text style={{ ...pdfStyles.body }}>{quote.client.destination_location.name}</Text>
                      <Text style={{ ...pdfStyles.body }}>{quote.client.destination_location.address}</Text>
                      <Text style={{ ...pdfStyles.body }}>
                        {quote.client.destination_location.city}
                        {quote.client.destination_location.region && `, ${quote.client.destination_location.region}`}
                        {quote.client.destination_location.postCode &&
                          `, ${quote.client.destination_location.postCode}`}
                      </Text>
                      <Text style={{ ...pdfStyles.body }}>{quote.client.destination_location.country}</Text>
                    </View>
                  )}
              </View>
            </View>
          </View>
          {quote.agent && quote.clientDisplay && quote.clientDisplay.agent === true && (
            <View style={{ ...pdfStyles.col, ...pdfStyles.agentBox }}>
              <Text style={{ ...pdfStyles.caption, ...pdfStyles.primaryText }}>AGENT</Text>
              <Text style={{ ...pdfStyles.body }}>{quote.agent.name}</Text>
              <Text style={{ ...pdfStyles.body }}>{quote.agent_staff}</Text>
              <Text style={{ ...pdfStyles.body }}>{quote.agent.address}</Text>
              <Text style={{ ...pdfStyles.body }}>{quote.agent.city}</Text>
              <Text style={{ ...pdfStyles.body }}>{quote.agent.country}</Text>
            </View>
          )}
        </View>

        <View style={{ ...pdfStyles.divider }}></View>

        {chargeCategories &&
          chargeCategories.map((chargeCategory) => {
            return (
              <PdfChargeSection
                baseCurrencyCode={currentFreightForwarder.baseCurrencyCode}
                theme={theme}
                key={chargeCategory.code}
                chargeCategory={chargeCategory}
                pdfStyles={pdfStyles}
                quote={quote}
                hasBaseFee={hasBaseFee}
                hasMinimum={hasMinimum}
              />
            );
          })}

        {quote.clientDisplay && quote.clientDisplay.grandTotal === true && (
          <View>
            <View style={{ ...pdfStyles.divider }}></View>

            <View style={{ ...pdfStyles.row, ...pdfStyles.marginTop }}>
              <View style={{ ...pdfStyles.col }}>
                <Text style={{ ...pdfStyles.h5, ...pdfStyles.textRight }}>
                  TOTAL: {displayCurrency?.code} ${calcSellTotal(quote.charges).toFixed(2)}
                </Text>
              </View>
            </View>
          </View>
        )}

        <View style={{ ...pdfStyles.divider }}></View>

        <View style={{ ...pdfStyles.row }}>
          {quote.clientDisplay.currencies ? (
            <View style={{ ...pdfStyles.col }}>
              <Text style={{ ...pdfStyles.subtitle2, ...pdfStyles.bold, ...pdfStyles.opacity1 }}>
                CURRENCY CONVERSIONS USED:
              </Text>
              {conversionFactorsUsed.map((conversion_factor) => {
                return (
                  <Text key={conversion_factor._id} style={{ ...pdfStyles.subtitle2, ...pdfStyles.opacity1 }}>
                    1 {currentFreightForwarder.baseCurrencyCode} = {(1 / conversion_factor.to_NZD).toFixed(2)}{' '}
                    {conversion_factor.code}
                  </Text>
                );
              })}
            </View>
          ) : null}
          {quote.notes && quote.clientDisplay.notes ? (
            <View style={{ ...pdfStyles.col }}>
              <Text style={{ ...pdfStyles.subtitle2, ...pdfStyles.bold, ...pdfStyles.opacity1 }}>NOTES:</Text>
              <Text style={{ ...pdfStyles.subtitle2, ...pdfStyles.opacity1 }}>{quote.notes}</Text>
            </View>
          ) : null}
        </View>

        <Text
          style={{ ...pdfStyles.subtitle2, ...pdfStyles.opacity1, ...pdfStyles.marginTop, ...pdfStyles.gutterBottom }}
        >
          TERMS & CONDITIONS
        </Text>

        <BreakText pdfStyles={pdfStyles} longText={quote.terms_and_conditions} />

        <Link break src={conditions_of_trade}>
          <Text
            style={{ ...pdfStyles.subtitle2, ...pdfStyles.opacity1, ...pdfStyles.marginTop, ...pdfStyles.gutterBottom }}
          >
            CONDITIONS OF TRADE (click to view)
          </Text>
        </Link>
        <Link break src={conditions_of_trade}>
          <Text style={{ ...pdfStyles.muted, ...pdfStyles.subtitle2 }}>{conditions_of_trade}</Text>
        </Link>
        <Text
          style={pdfStyles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} of ${totalPages} pages`}
          fixed
        />
      </PageX>
    </DocumentX>
  );
};

const BreakText = ({ pdfStyles, longText }: { pdfStyles: ReactPDF.Styles; longText: string }) => {
  const textArray = longText?.match(/(.|[\r\n]){1,8000}/g);

  return (
    <View>
      {textArray &&
        textArray.map((shortText, index) => {
          return (
            <Text key={index} style={{ ...pdfStyles.subtitle2 }}>
              {shortText}
            </Text>
          );
        })}
    </View>
  );
};

type Column = { name: string; width: number; contents: (charge: Partial<QuoteCharge>) => JSX.Element | null };
const ChargeRow = ({
  charge,
  pdfStyles,
  columns,
}: {
  charge: Partial<QuoteCharge>;
  pdfStyles: ReactPDF.Styles;
  columns: Column[];
}) => {
  return (
    <View wrap={false} style={{ ...pdfStyles.row, ...pdfStyles.tableBody }} key={charge._id}>
      {columns.map((column) => (
        <View key={column.name} style={{ ...pdfStyles.tableCell, width: column.width - 2 }}>
          {column.contents(charge)}
        </View>
      ))}
    </View>
  );
};

type PdfChargeSectionProps = {
  baseCurrencyCode: string;
  chargeCategory: ChargeCategory;
  pdfStyles: ReactPDF.Styles;
  quote: Quote;
  theme: Theme;
  hasBaseFee: boolean;
  hasMinimum: boolean;
};
const PdfChargeSection = ({
  chargeCategory,
  pdfStyles,
  quote,
  theme,
  hasBaseFee,
  hasMinimum,
}: PdfChargeSectionProps) => {
  const { calcSellTotal, displayCurrency } = useQuoteCurrencyConversion(quote, quote.freightForwarder);

  const mode = quote.mode;
  const showSubTotal = quote.clientDisplay && quote.clientDisplay.subTotal;
  const showCalculatedSell = quote.clientDisplay && quote.clientDisplay.calculatedSell;

  const charges = quote.charges.filter((c) => {
    return c.charge_category_code === chargeCategory.code && (c.sellRate > 0 || c.sellMinimum > 0 || c.sellBaseFee > 0);
  });

  const totalWithAvailable = 595.28 - theme.spacing(4); // A4 width minus padding on both sides
  const columns: Column[] = [
    { name: 'CHARGES', width: 243, contents: (charge: Partial<QuoteCharge>) => <Text>{charge.description}</Text> },
    { name: 'UNIT', width: 120, contents: (charge: Partial<QuoteCharge>) => <Text>{charge.unit_name}</Text> },
  ];
  if (hasBaseFee) {
    columns.push({
      name: 'BASE FEE',
      width: 100,
      contents: (charge) => (
        <Text>
          {charge.sellBaseFee && charge.sellBaseFee > 0
            ? `${charge.currency?.code} $${(charge.sellBaseFee || 0).toFixed(2)}`
            : null}
        </Text>
      ),
    });
  }
  if (hasMinimum) {
    columns.push({
      name: 'MINIMUM',
      width: 100,
      contents: (charge) => (
        <Text>
          {charge.sellMinimum && charge.sellMinimum > 0
            ? `${charge.currency?.code} $${charge.sellMinimum.toFixed(2)}`
            : null}
        </Text>
      ),
    });
  }

  if (mode === 'AIR') {
    columns.push({
      name: 'UNIT PRICE',
      width: 100,
      contents: (charge) =>
        charge.sellRate && charge.sellRate > 0 ? (
          <Text>{`${charge.currency?.code} $${charge.sellRate.toFixed(2)}`}</Text>
        ) : null,
    });
  } else {
    columns.push(
      ...[
        {
          name: 'UNIT PRICE',
          width: 100,
          contents: (charge: Partial<QuoteCharge>) => (
            <Text>{`${charge.currency?.code} $${(charge.sellRate || 0).toFixed(2)}`}</Text>
          ),
        },
      ]
    );
  }
  if (showCalculatedSell) {
    columns.push({
      name: 'AMOUNT',
      width: 100,
      contents: (charge) =>
        charge.calculatedSell && charge.calculatedSell > 0 ? (
          <Text>{`${charge.currency?.code} $${charge.calculatedSell.toFixed(2)}`}</Text>
        ) : null,
    });
  }
  const totalWithUsed = columns.reduce((accum, col) => {
    return accum + col.width;
  }, 0);

  const numberColScale = totalWithAvailable / totalWithUsed;

  columns.forEach((col) => (col.width *= numberColScale));

  const overallCharge = quote && quote.service && { ...quote.service.overallCharge };

  return charges && charges.length > 0 ? (
    <View>
      <View wrap={false}>
        <View style={{ ...pdfStyles.row, ...pdfStyles.tableHeaderRow }}>
          {columns.map((column, index) => (
            <Text key={column.name} style={{ ...pdfStyles.tableCellHead, width: column.width }}>
              {index === 0 && `${chargeCategory.name.toUpperCase()} `}
              {column.name}
            </Text>
          ))}
        </View>
        {overallCharge && overallCharge.sellMinimum && overallCharge.sellMinimum > 0 ? (
          <ChargeRow
            columns={columns}
            charge={{
              ...overallCharge,
              description: 'Overall Charge Minimum',
              calculatedSell: overallCharge.sellMinimum,
            }}
            pdfStyles={pdfStyles}
          ></ChargeRow>
        ) : null}

        {charges && charges.length > 0 && (
          <ChargeRow columns={columns} charge={charges[0]} pdfStyles={pdfStyles}></ChargeRow>
        )}
      </View>
      {charges.map((charge, index) => {
        return index === 0 ? null : (
          <ChargeRow key={index} charge={charge} columns={columns} pdfStyles={pdfStyles}></ChargeRow>
        );
      })}
      {showSubTotal === true ? (
        <View style={{ ...pdfStyles.row, ...pdfStyles.marginTop }}>
          <View style={{ ...pdfStyles.col }}>
            <Text style={{ ...pdfStyles.h5, ...pdfStyles.textRight }}>
              Subtotal: {displayCurrency?.code} ${calcSellTotal(charges).toFixed(2)}
            </Text>
          </View>
        </View>
      ) : null}
    </View>
  ) : null;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    pdfWrapper: {
      display: 'block',
      width: '80vh',
      height: '80vh',
    },
    externalLink: {
      marginRight: theme.spacing(5),
    },
    buttonRow: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    leftIcon: {
      marginRight: theme.spacing(1),
    },
  })
);

type Props = { quote_id: string; freight_forwarder_id: string };

const CustomerViewContents = ({ quote_id, freight_forwarder_id }: Props) => {
  const classes = useStyles();
  const { updateTheme, theme }: { updateTheme: (theme: any) => void; theme: any } = useContext(ThemeContext);
  const firebase = useContext(FirebaseContext);

  const [themeLoaded, setThemeLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState<Quote | undefined>(undefined);
  const [pdfBlob, setPdfBlob] = useState<Blob | undefined>(undefined);
  const [numPages, setNumPages] = useState<number | undefined>(undefined);
  const [pageRendered, setPageRendered] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const primaryColor = quote?.freightForwarder?.primaryColor;

  useEffect(() => {
    firebase.quote(freight_forwarder_id, quote_id, { publicRoute: true }).then((quote: Quote) => {
      setQuote(quote);
    });
  }, [quote_id, firebase, freight_forwarder_id]);

  useEffect(() => {
    if (primaryColor && theme.palette.primary.main !== primaryColor) {
      updateTheme({
        ...theme,
        palette: {
          ...theme.palette,
          primary: {
            ...theme.palette.primary,
            main: primaryColor,
            dark: darken(primaryColor, 0.2),
            darker: darken(primaryColor, 0.4),
            light: lighten(primaryColor, 0.2),
            lighter: lighten(primaryColor, 0.9),
          },
        },
      });
    }
    if (primaryColor) {
      // set the theme as loaded once the primary color has been set
      // must do this as the them update is not reflected in the pdf blob dynamically
      // i.e. we wait for the theme to be set before creating the blob
      setThemeLoaded(true);
    }
  }, [primaryColor, theme, updateTheme]);

  const createBlob = useCallback(
    (quote: Quote) => {
      const { chargeCategories } = quote.constants;
      const { charges = [], freightForwarder: currentFreightForwarder } = quote;
      let hasBaseFee = false;
      let hasMinimum = false;
      charges.forEach((charge) => {
        if (hasBaseFee === false && charge.sellBaseFee && charge.sellBaseFee > 0) {
          hasBaseFee = true;
        }
        if (hasMinimum === false && charge.sellMinimum && charge.sellMinimum > 0) {
          hasMinimum = true;
        }
        const currency = currentFreightForwarder.currencies.find((c) => c._id === charge.currency_id);
        let conversionFactor;
        if (currency) {
          // get the conversionFactor based on the rates stored at the time the quote was created
          conversionFactor = quote.conversion_factors.find((cf) => cf.code === currency?.code);
        }
        // Assign the currency to use for the charge calculations
        charge.currency = conversionFactor ||
          currency || { code: quote.displayCurrencyCode || currentFreightForwarder.baseCurrencyCode, to_NZD: 1 };
      });

      const overallCharge = quote && quote.service && quote.service.overallCharge;
      if (overallCharge) {
        overallCharge.currency = currentFreightForwarder.currencies.find((c) => c._id === overallCharge.currency_id);
      }

      pdf(
        <MyDocument
          theme={theme}
          quote={quote}
          currentFreightForwarder={currentFreightForwarder}
          chargeCategories={chargeCategories}
          hasBaseFee={hasBaseFee}
          hasMinimum={hasMinimum}
        />
      )
        .toBlob()
        .then(setPdfBlob)
        .finally(() => {
          setLoading(false);
        });
    },
    [theme]
  );
  useEffect(() => {
    // only create the blob if the theme has loaded and the quote has been set
    if (themeLoaded && quote) {
      createBlob(quote);
    }
  }, [themeLoaded, quote, createBlob]);

  const download = (blob: Blob, filename: string) => {
    // IE11 downloadblob
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, filename);
    } else {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', filename);
      link.click();
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const changePdfPage = (changeBy: number) => {
    setPageNumber((pageNumber) => {
      pageNumber += changeBy;
      pageNumber = Math.max(0, pageNumber);
      pageNumber = Math.min(pageNumber, numPages || 0);
      return pageNumber;
    });
    setPageRendered(false);
  };

  const handleBookClick = () => {
    firebase.quoteBook(quote);
  };

  const renderButtonRow = (
    pdfBlob: Blob,
    quote: Quote,
    classes: any,
    pageNumber: number,
    numPages: number,
    loading: boolean
  ) => (
    <Box className={classes.buttonRow}>
      <Button
        onClick={() => download(pdfBlob, `${quote.quoteNumber}.pdf`)}
        disabled={loading}
        variant="contained"
        color="primary"
        className={classes.externalLink}
      >
        <FontAwesomeIcon icon={faDownload as IconProp} className={classes.leftIcon} />
        Download
      </Button>
      <Button
        onClick={handleBookClick}
        href={`mailto:${quote.prepared_by_email}`}
        disabled={!quote.prepared_by_email}
        target="_blank"
        className={classes.externalLink}
        variant="contained"
        color="primary"
      >
        <FontAwesomeIcon icon={faEnvelope as IconProp} className={classes.leftIcon} />
        Book Now
      </Button>
      <Button
        onClick={() => changePdfPage(-1)}
        disabled={pageNumber <= 1}
        variant="contained"
        color="primary"
        className={classes.externalLink}
      >
        <FontAwesomeIcon icon={faBackward as IconProp} className={classes.leftIcon} />
        Previous Page
      </Button>
      <Button
        onClick={() => changePdfPage(1)}
        disabled={pageNumber >= numPages}
        variant="contained"
        color="primary"
        className={classes.externalLink}
      >
        <FontAwesomeIcon icon={faForward as IconProp} className={classes.leftIcon} />
        Next Page
      </Button>
    </Box>
  );

  return (
    <React.Fragment>
      <Spinner visible={loading} />
      {quote && loading === false && pdfBlob && (
        <React.Fragment>
          <br />
          {renderButtonRow(pdfBlob, quote, classes, pageNumber, numPages || 0, loading)}
          <RpDocument file={pdfBlob} onLoadSuccess={onDocumentLoadSuccess}>
            <RpPage pageNumber={pageNumber} onRenderSuccess={() => setPageRendered(true)} />
          </RpDocument>
          {pageRendered ? renderButtonRow(pdfBlob, quote, classes, pageNumber, numPages || 0, loading) : null}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default CustomerViewContents;
